import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useInterval } from 'usehooks-ts';

import { vipBenefitsConfig } from '@/common/config/vip-benefirs';
import { calcSecondsToDate } from '@/common/lib/time';
import { timeStringFormat } from '@/common/lib/timer';
import { Glow, ShopBuyButton } from '@/components/Shop/ui/ShopUi';
import { TabsContext } from '@/components/Tabs/Tabs';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { Benefits } from './Benefits/Benefits';

import css from './styles.module.scss';

import characterPng from '/img/shop/v2/vip/character.png';
import characterWebp from '/img/shop/v2/vip/character.webp';
import officePng from '/img/shop/v2/vip/office.png';
import officeWebp from '/img/shop/v2/vip/office.webp';

const BENEFIT_IDS = [0, 3, 4, 5, 6];

/** "VIP Offer" card of the Offers tab; buying happens on the VIP tab. */
const VipMarket: FC = () => {
  const { t } = useTranslation();
  const validUntil = useAppSelector((state) => state.user.vip?.validUntil);
  const [vipSecondsLeft, setVipSecondsLeft] = useState(() =>
    calcSecondsToDate(validUntil),
  );
  useInterval(
    () => setVipSecondsLeft(calcSecondsToDate(validUntil)),
    validUntil ? 1000 : null,
  );
  const { setActiveTabId } = useContext(TabsContext);
  const [activeSlide, setActiveSlide] = useState(0);

  const timeToVipEnd =
    vipSecondsLeft >= 86400
      ? timeStringFormat(vipSecondsLeft * 1000, {
          template: '{days}d : {hours}h',
        })
      : timeStringFormat(vipSecondsLeft * 1000, {
          template: '{hours}h : {minutes}m',
        });

  return (
    <div className={css.vip}>
      <div className={css.bg}>
        <Picture
          img={officePng}
          webp={officeWebp}
          alt=""
          className={css.office}
        />
        <Glow color="#FFD600" className="opacity-50 size-40 -left-20 -top-20" />
        <Glow
          color="#FFD600"
          className="opacity-20 size-40 left-1/2 -translate-x-1/2 -bottom-16"
        />
        <Picture
          img={characterPng}
          webp={characterWebp}
          alt=""
          className={css.character}
        />
      </div>

      <div className={css.timer}>
        {vipSecondsLeft ? (
          timeToVipEnd
        ) : (
          <span className="text-white/60">{t('Inactive')}</span>
        )}
      </div>

      <h3 className={css.title}>{t('Join the VIP Club')}</h3>

      <div className={css.swiperWrapper}>
        <Swiper
          slidesPerView={1}
          loop
          autoplay={{ delay: 2500, disableOnInteraction: false }}
          modules={[Autoplay]}
          onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
        >
          {BENEFIT_IDS.map((id) => (
            <SwiperSlide key={id}>
              <Benefits
                icon={vipBenefitsConfig.shopIcons[id]}
                transKey={vipBenefitsConfig.shopTransKeyObjBenefits[id](t)}
                classNames="items-center [&>p]:!text-sm [&>p]:!leading-none [&>p]:font-medium [&>p]:whitespace-nowrap"
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {/* Own dots: Swiper's pagination styling was not reliable here */}
        <div className={css.dots}>
          {BENEFIT_IDS.map((_, index) => (
            <span
              key={index}
              className={cn(css.dot, index === activeSlide && css.dotActive)}
            />
          ))}
        </div>
      </div>

      <div className={css.buttons}>
        <ShopBuyButton
          label={t('Join now')}
          tag="-50%"
          wrapperClassName="flex-1"
          onClick={() => setActiveTabId('vip')}
        />
      </div>
    </div>
  );
};

export { VipMarket };
